import React from 'react';
import { Alert, Text } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';

/**
 * Cibles perdues d'un `Select` de sidebar — une page supprimée, un lien retiré
 * de la banque, une variable disparue du CRM.
 *
 * Extrait de `ButtonLinkField` : le champ d'adresse du bloc « Contenu externe »
 * a exactement le même besoin, et les deux doivent le dire de la même façon.
 */

/** Toutes les options, groupes aplatis. */
export function flatOptions(options) {
  return options.flatMap((entry) => (entry.items ? entry.items : [entry]));
}

/** Une cible choisie qui ne figure plus dans les options. */
export function isLost(options, value) {
  return !!value && !flatOptions(options).some((item) => item.value === value);
}

/**
 * La valeur perdue, ajoutée aux options pour que le `Select` continue de
 * l'afficher. Sans elle, le champ paraîtrait simplement vide — impossible de
 * distinguer « rien n'a jamais été choisi » de « la cible a disparu ».
 */
export function withLostOption(options, value, targetsKnown, label) {
  if (!targetsKnown || !isLost(options, value)) return options;
  return [...options, { group: 'Introuvable', items: [{ value, label: `${label} — ${value}` }] }];
}

/**
 * L'option choisie, réinjectée dans la liste quand le filtre l'en écarte.
 *
 * Un sélecteur filtré (les variables qui contiennent une adresse, les liens
 * intégrables) ne contient pas forcément ce qui a déjà été choisi — une
 * variable encore vide, par exemple. Sans elle, le `Select` afficherait la
 * **clé brute**, illisible, et le champ passerait pour cassé alors que la
 * cible existe.
 */
export function withChosenOption(options, value, label) {
  if (!value || !isLost(options, value)) return options;
  return [...options, { value, label: label || value }];
}

/**
 * `consequence` : un lien de banque supprimé ne casse rien sur le site publié
 * — son adresse a été copiée — alors qu'une page ou un bloc supprimés laissent
 * le bouton sans cible. Le même bandeau ne peut donc pas dire la même chose.
 */
export function LostTargetAlert({ shown, what, consequence }) {
  if (!shown) return null;
  return (
    <Alert
      color="orange"
      variant="light"
      p="xs"
      icon={<FontAwesomeIcon icon={icon({ name: 'triangle-exclamation', style: 'light' })} style={{ fontSize: 16 }} />}
    >
      <Text size="xs">
        {what}
        {consequence
          || '. Sur le site publié, le bouton ne mène nulle part : choisissez une autre cible.'}
      </Text>
    </Alert>
  );
}
